// components/roles/RoleModal.tsx
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Loader2, KeyRound } from "lucide-react";
import { Role } from "@/types/roles";
import { PermissionsSelector } from "./PermissionsSelector";

/**
 * Convierte los permisos de Role a un arreglo de strings.
 * - Si es un array, se devuelve tal cual.
 * - Si es un objeto con { all: true }, se devuelve ["*"].
 * - Si es un objeto con claves booleanas, devuelve solo las claves con true.
 */
const normalizePermissions = (permissions: Role["permissions"]): string[] => {
  if (Array.isArray(permissions)) return permissions;

  if (typeof permissions === "object" && permissions !== null) {
    if (permissions.all === true) return ["*"];

    return Object.entries(permissions)
      .filter(([_, value]) => value === true)
      .map(([key]) => key);
  }

  return [];
};

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (roleData: Partial<Role>) => Promise<void>;
  role?: Role | null;
  companySlug: string;
  companyId: string;
  loading?: boolean;
}

export function RoleModal({
  isOpen,
  onClose,
  onSave,
  role,
  companySlug,
  companyId,
  loading = false,
}: RoleModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>("info");
  const [errors, setErrors] = useState<{ name?: string; permissions?: string }>(
    {}
  );

  // Inicializa los campos cuando cambia el rol o se abre el modal
  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description || "");
      setSelectedPermissions(normalizePermissions(role.permissions));
    } else {
      setName("");
      setDescription("");
      setSelectedPermissions([]);
    }
    setErrors({});
  }, [role, isOpen]);

  // Validación simple del formulario
  const validateForm = () => {
    const newErrors: typeof errors = {};
    if (!name.trim()) newErrors.name = "El nombre del rol es requerido";
    if (selectedPermissions.length === 0)
      newErrors.permissions = "Debe seleccionar al menos un permiso";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const permissionsValue = selectedPermissions.includes("*")
        ? { all: true }
        : selectedPermissions;

      const roleData: Partial<Role> = {
        name: name.trim(),
        description: description.trim(),
        permissions: permissionsValue,
        slug: companySlug,
        company_id: companyId,
        is_system_role: role?.is_system_role || false,
      };

      await onSave(roleData);
      onClose();
    } catch (error) {
      console.error("Error al guardar rol:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={loading ? undefined : onClose}>
      <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">
        <div className="flex flex-col h-full overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col h-full overflow-hidden">
            <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4 z-10 relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <DialogHeader>
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                    <Shield className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                      {role ? "Editar Rol" : "Nuevo Rol"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 mt-0.5">
                      {role
                        ? "Modifica los detalles y permisos del rol."
                        : "Define un nuevo rol y sus permisos asociados."}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2 bg-slate-100/80 p-1.5 rounded-xl h-10">
                <TabsTrigger value="info" className="rounded-lg px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm font-medium transition-all text-sm">Información</TabsTrigger>
                <TabsTrigger value="permissions" className="rounded-lg px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm font-medium transition-all text-sm">Permisos</TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50">
              <div className="space-y-8">
                <TabsContent value="info" className="space-y-6 mt-0 focus:outline-none animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre del Rol *</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ej: Gerente de Ventas"
                      className={errors.name ? "border-red-500" : ""}
                      disabled={loading}
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? "name-error" : undefined}
                    />
                    {errors.name && (
                      <p id="name-error" className="text-red-500 text-sm">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descripción</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe las responsabilidades de este rol..."
                      rows={4}
                      disabled={loading}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="permissions" className="space-y-6 mt-0 focus:outline-none animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 text-slate-800 font-semibold mb-2">
                      <KeyRound className="h-5 w-5 text-indigo-500" />
                      Permisos y Accesos
                    </div>
                    <PermissionsSelector
                      selectedPermissions={selectedPermissions}
                      onPermissionsChange={setSelectedPermissions}
                      error={errors.permissions}
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      <strong>Nota:</strong> El permiso especial "*" representa acceso
                      completo a todos los permisos del sistema.
                    </p>
                  </div>
                </TabsContent>
              </div>
            </div>

            <div className="bg-white border-t border-slate-100 p-4 sm:px-6 sm:py-4 flex-shrink-0">
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                  className="w-full sm:w-auto rounded-xl border-slate-200 hover:bg-slate-50 text-slate-700 font-medium transition-all h-11 px-6"
                >
                  Cancelar
                </Button>
                {activeTab === "info" ? (
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("permissions");
                    }}
                    disabled={loading}
                    className="w-full sm:w-auto bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-lg font-medium transition-all h-11 px-8"
                  >
                    Siguiente
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSave();
                    }}
                    disabled={loading}
                    className="w-full sm:w-auto bg-slate-900 hover:bg-black text-white rounded-xl shadow-lg shadow-slate-900/20 font-medium transition-all h-11 px-8"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Guardando...
                      </>
                    ) : role ? (
                      "Actualizar Rol"
                    ) : (
                      "Crear Rol"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
